import { useCallback, useMemo, useState } from "react";
import { useAdministrationMenuItems } from "../hooks/useAdministrationMenuItems";
import { useAgreementManagementItems } from "../hooks/useAgreementManagementItems";
import { useDepartmentBasedProjectMenuItems } from "../hooks/useDepartmentBasedProjectMenuItems";
import { useDictionariesMenuItems } from "../hooks/useDictionariesMenuItems";
import { useMainMenuItems } from "../hooks/useMainMenuItems";
import { usePayrollMenuItems } from "../hooks/usePayrollMenuItems";
import { useProjectMenuItems } from "../hooks/useProjectMenuItems";
import { useReportsMenuItems } from "../hooks/useReportsMenuItems";
import { useStructureAssignmentsMenuItems } from "../hooks/useStructureAssignmentsMenuItems";
import { useUsersMenuItems } from "../hooks/useUsersMenuItems";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useMenuSearch } from "../hooks/useMenuSearch";
import { NavigationAnchor } from "@/components/layout/types";

export enum NAVIGATION_GROUP {
  MAIN = "main",
  USERS = "users",
  ADMINISTRATION = "administration",
  DICTIONARIES = "dictionaries",
  PROJECTS = "projects",
  DEPARTMENT_BASED_PROJECTS = "departmentBasedProjects",
  STRUCTURE_ASSIGNMENTS = "structureAssignments",
  AGREEMENT_MANAGEMENT = "agreementManagement",
  PAYROLLS = "payrolls",
  REPORTS = "reports",
}

export const useAppNavigation = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const [expanded, setExpanded] = useLocalStorage<Record<string, boolean>>(
    "sidebar-expanded",
    {
      dictionaries: false,
    },
  );

  const mainItems = useMainMenuItems();
  const usersItems = useUsersMenuItems();
  const administrationItems = useAdministrationMenuItems();
  const dictionariesItems = useDictionariesMenuItems();
  const projectsItems = useProjectMenuItems();
  const payrollItems = usePayrollMenuItems();
  const departmentBasedProjectsItems = useDepartmentBasedProjectMenuItems();
  const structureAssignmentsItems = useStructureAssignmentsMenuItems();
  const agreementManagementItems = useAgreementManagementItems();
  const reportsItems = useReportsMenuItems();

  const groups = useMemo(
    () => ({
      [NAVIGATION_GROUP.MAIN]: mainItems,
      [NAVIGATION_GROUP.USERS]: usersItems,
      [NAVIGATION_GROUP.ADMINISTRATION]: administrationItems,
      [NAVIGATION_GROUP.DICTIONARIES]: dictionariesItems,
      [NAVIGATION_GROUP.PROJECTS]: projectsItems,
      [NAVIGATION_GROUP.PAYROLLS]: payrollItems,
      [NAVIGATION_GROUP.DEPARTMENT_BASED_PROJECTS]:
        departmentBasedProjectsItems,
      [NAVIGATION_GROUP.STRUCTURE_ASSIGNMENTS]: structureAssignmentsItems,
      [NAVIGATION_GROUP.AGREEMENT_MANAGEMENT]: agreementManagementItems,
      [NAVIGATION_GROUP.REPORTS]: reportsItems,
    }),
    [
      mainItems,
      usersItems,
      administrationItems,
      dictionariesItems,
      projectsItems,
      payrollItems,
      departmentBasedProjectsItems,
      structureAssignmentsItems,
      agreementManagementItems,
      reportsItems,
    ],
  );

  const visibleMenuGroups = useMemo(
    () => excludeHiddenItemsFromGroups(groups),
    [groups],
  );

  const searchResults = useMenuSearch(visibleMenuGroups, searchQuery);

  const onOpenChange = useCallback(
    (id: string, isOpen: boolean) => {
      setExpanded((prev) => ({ ...prev, [id]: isOpen }));
    },
    [setExpanded],
  );

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value.toLowerCase());
    },
    [setSearchQuery],
  );

  return {
    onOpenChange,
    onSearchChange,
    searchResults,
    searchQuery,
    expanded,
  };
};

const excludeHiddenItemsFromGroups = (
  groups: Record<string, NavigationAnchor[]>,
): Record<string, NavigationAnchor[]> =>
  Object.entries(groups).reduce(
    (acc, [groupKey, items]) => ({
      ...acc,
      [groupKey]: items.filter((item) => !item.hide),
    }),
    {},
  );
